import * as Sentry from '@sentry/vue'
import PricingLineupApi from '@/api/pricingLineup'

// AHA-42054 — derives whether the Essential plan is absent from the lineup THIS
// viewer would see on the pricing page, by reading the shared
// GET /api/pricing/lineup. Single source of truth: the presenter never re-evaluates
// the Unleash flag itself, so it can never disagree with the pricing page about a
// given session/country. The `*_planName` essential→pro remap (plan.utils) keys off
// `isEssentialExcluded`.
//
// It ALSO exposes the per-plan/per-cycle Stripe price `lookupKey` the viewer sees
// on the pricing page (`priceLookupKeys`). The presenter upgrade flow forwards that
// lookupKey to the variant-aware v4 checkout so the price charged matches the price
// advertised on the pricing page (it would otherwise resolve the base price from a
// canonical `pro-yearly` key and diverge from a running price experiment).

const SET_LINEUP = 'SET_LINEUP'

const _state = {
  loaded: false,
  essentialExcluded: false,
  // { [plan]: { monthly?: lookupKey, yearly?: lookupKey } } — empty until loaded.
  priceLookupKeys: {},
}

// Reduce the lineup products to a { plan: { cycle: lookupKey } } map. Only products
// that actually carry a priced cycle contribute; free/enterprise (no prices) and any
// malformed entry are skipped, so a lookup simply misses and the caller falls back.
const buildPriceLookupKeys = (products) => {
  const map = {}
  if (!Array.isArray(products)) return map
  products.forEach((product) => {
    if (!product || !product.plan || !product.prices) return
    const cycles = {}
    Object.keys(product.prices).forEach((cycle) => {
      const lookupKey = product.prices[cycle] && product.prices[cycle].lookupKey
      if (lookupKey) cycles[cycle] = lookupKey
    })
    if (Object.keys(cycles).length) map[product.plan] = cycles
  })
  return map
}

const getters = {
  // True only once the lineup has loaded AND it has products none of which is the
  // essential tier. Defaults to false (Essential visible = current behaviour) while
  // loading and on any failure, so the funnel never hides a tier we're unsure about.
  isEssentialExcluded(state) {
    return state.essentialExcluded
  },
  isPricingLineupLoaded(state) {
    return state.loaded
  },
  // (plan, cycle) → the variant Stripe lookupKey the pricing page shows, or null.
  // Null means "no variant price known" → caller keeps its canonical behaviour.
  variantPriceLookupKey: (state) => (plan, cycle) => {
    if (!plan || !cycle) return null
    const byCycle = state.priceLookupKeys[plan]
    return (byCycle && byCycle[cycle]) || null
  },
}

// Module-level coalescing: the lineup is viewer-global (not per-component), so
// concurrent callers during bootstrap share one in-flight request.
let inflight = null

const actions = {
  async fetchPricingLineup({ commit, state }) {
    if (state.loaded) return state.essentialExcluded
    if (inflight) return inflight

    inflight = (async () => {
      try {
        const products = await PricingLineupApi.getPricingLineupProducts()
        const essentialExcluded =
          Array.isArray(products)
          && products.length > 0
          && !products.some((product) => product && product.plan === 'essential')
        commit(SET_LINEUP, { essentialExcluded, priceLookupKeys: buildPriceLookupKeys(products) })
        return essentialExcluded
      } catch (error) {
        // Safe default: keep Essential visible on any failure so we never break the
        // upgrade funnel because the (non-critical) lineup read failed.
        Sentry.captureException(error)
        commit(SET_LINEUP, { essentialExcluded: false, priceLookupKeys: {} })
        return false
      } finally {
        inflight = null
      }
    })()

    return inflight
  },
}

/* eslint-disable no-param-reassign */
const mutations = {
  [SET_LINEUP](state, { essentialExcluded, priceLookupKeys }) {
    state.essentialExcluded = essentialExcluded
    state.priceLookupKeys = priceLookupKeys || {}
    state.loaded = true
  },
}
/* eslint-enable no-param-reassign */

export default {
  namespaced: true,
  state: _state,
  getters,
  actions,
  mutations,
}
